import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl, { type GeoJSONSource, type LngLatBoundsLike } from "maplibre-gl";
import type { OcpiLocation } from "../types/ocpi";
import {
  aggregateStatus,
  formatKw,
  locCoord,
  locationMaxKw,
  locationTier,
} from "../utils/format";
import { BRAND } from "../config/brand";
import {
  BASE_STYLE,
  INITIAL_CENTER,
  INITIAL_ZOOM,
  MAX_ZOOM,
  MIN_ZOOM,
  TR_MAX_BOUNDS,
} from "../map/style";
import { Legend } from "./Legend";
import { useI18n } from "../i18n/LanguageContext";

const SRC = "stations";
const LYR_CLUSTERS = "stations-clusters";
const LYR_CLUSTER_COUNT = "stations-cluster-count";
const LYR_POINTS_HALO = "stations-points-halo";
const LYR_POINTS = "stations-points";
const LYR_POINTS_CORE = "stations-points-core";
const LYR_SELECTED_RING = "stations-selected-ring";
const LYR_SELECTED = "stations-selected";

interface PointProps {
  id: string;
  name: string;
  tier: "AC" | "DC_FAST" | "DC_ULTRA";
  kw: number;
  status: string;
  groupCount: number;
}

function toFeatureCollection(locations: OcpiLocation[]): GeoJSON.FeatureCollection {
  // Aggregate stations sharing the exact same coordinate into a single
  // feature, but keep a `groupCount` so the UI can surface that.
  const buckets = new Map<string, OcpiLocation[]>();
  for (const loc of locations) {
    const { lat, lng } = locCoord(loc);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    const key = `${lng.toFixed(6)},${lat.toFixed(6)}`;
    const arr = buckets.get(key);
    if (arr) arr.push(loc);
    else buckets.set(key, [loc]);
  }

  const features: GeoJSON.Feature[] = [];
  for (const [key, locs] of buckets) {
    const [lngStr, latStr] = key.split(",");
    const lng = parseFloat(lngStr);
    const lat = parseFloat(latStr);
    const rep = locs
      .slice()
      .sort((a, b) => {
        const ta = tierRank(locationTier(a));
        const tb = tierRank(locationTier(b));
        if (ta !== tb) return tb - ta;
        return locationMaxKw(b) - locationMaxKw(a);
      })[0];
    const props: PointProps = {
      id: rep.id,
      name: locs.length > 1 ? `${rep.name ?? rep.id} (+${locs.length - 1})` : (rep.name ?? rep.id),
      tier: locationTier(rep),
      kw: locationMaxKw(rep),
      status: aggregateStatus(rep),
      groupCount: locs.length,
    };
    features.push({
      type: "Feature",
      id: rep.id,
      geometry: { type: "Point", coordinates: [lng, lat] },
      properties: props as unknown as GeoJSON.GeoJsonProperties,
    });
  }
  return { type: "FeatureCollection", features };
}

function tierRank(t: "AC" | "DC_FAST" | "DC_ULTRA"): number {
  return t === "DC_ULTRA" ? 3 : t === "DC_FAST" ? 2 : 1;
}

export function StationsMap({
  locations,
  selectedId,
  onSelect,
  userCoords,
  onLocateClick,
  locateActive,
  locateBusy,
}: {
  locations: OcpiLocation[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  userCoords: { lat: number; lng: number; accuracy: number } | null;
  onLocateClick: () => void;
  locateActive: boolean;
  locateBusy: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const userMarkerRef = useRef<maplibregl.Marker | null>(null);
  const userFlewToRef = useRef(false);
  const hoverPopupRef = useRef<maplibregl.Popup | null>(null);
  const fitDoneRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const [styleReady, setStyleReady] = useState(false);
  const { t } = useI18n();

  const fc = useMemo(() => toFeatureCollection(locations), [locations]);

  // ─────────────────────────── Map init ───────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASE_STYLE,
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
      minZoom: MIN_ZOOM,
      maxZoom: MAX_ZOOM,
      maxBounds: TR_MAX_BOUNDS as LngLatBoundsLike,
      attributionControl: false,
      renderWorldCopies: false,
    });
    mapRef.current = map;

    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");

    map.on("load", () => {
      map.addSource(SRC, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
        cluster: true,
        clusterRadius: 50,
        clusterMaxZoom: 14,
      });

      // ── Cluster halo (soft outer glow) ─────────────────────────
      map.addLayer({
        id: LYR_CLUSTERS + "-halo",
        type: "circle",
        source: SRC,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": BRAND.primary,
          "circle-opacity": 0.18,
          "circle-blur": 0.6,
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["get", "point_count"],
            2, 26,
            10, 34,
            25, 42,
            50, 50,
            100, 60,
            250, 72,
          ],
        },
      });

      // ── Cluster bubble ─────────────────────────────────────────
      map.addLayer({
        id: LYR_CLUSTERS,
        type: "circle",
        source: SRC,
        filter: ["has", "point_count"],
        paint: {
          "circle-color": BRAND.primary,
          "circle-opacity": 0.96,
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["get", "point_count"],
            2, 18,
            10, 22,
            25, 26,
            50, 30,
            100, 36,
            250, 44,
          ],
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 3,
          "circle-stroke-opacity": 0.95,
        },
      });

      // ── Cluster count label ────────────────────────────────────
      map.addLayer({
        id: LYR_CLUSTER_COUNT,
        type: "symbol",
        source: SRC,
        filter: ["has", "point_count"],
        layout: {
          "text-field": ["get", "point_count_abbreviated"],
          "text-size": [
            "interpolate",
            ["linear"],
            ["get", "point_count"],
            2, 11,
            25, 12,
            100, 13,
            250, 14,
          ],
          "text-font": ["Noto Sans Bold"],
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color": "#ffffff",
        },
      });

      // ── Unclustered: halo · point · core ────────────────────────
      map.addLayer({
        id: LYR_POINTS_HALO,
        type: "circle",
        source: SRC,
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": 16,
          "circle-color": tierColorExpr(),
          "circle-opacity": 0.18,
          "circle-blur": 0.4,
        },
      });
      map.addLayer({
        id: LYR_POINTS,
        type: "circle",
        source: SRC,
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": 9,
          "circle-color": tierColorExpr(),
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2.6,
          "circle-stroke-opacity": 1,
        },
      });
      map.addLayer({
        id: LYR_POINTS_CORE,
        type: "circle",
        source: SRC,
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-radius": 2.6,
          "circle-color": "#ffffff",
        },
      });

      // ── Selected highlight (filter starts empty) ───────────────
      map.addLayer({
        id: LYR_SELECTED_RING,
        type: "circle",
        source: SRC,
        filter: ["==", ["get", "id"], "__none__"],
        paint: {
          "circle-radius": 22,
          "circle-color": BRAND.primary,
          "circle-opacity": 0.16,
          "circle-stroke-color": BRAND.primary,
          "circle-stroke-width": 2,
          "circle-stroke-opacity": 0.6,
        },
      });
      map.addLayer({
        id: LYR_SELECTED,
        type: "circle",
        source: SRC,
        filter: ["==", ["get", "id"], "__none__"],
        paint: {
          "circle-radius": 11,
          "circle-color": tierColorExpr(),
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 3.5,
        },
      });

      // ── Interactions ───────────────────────────────────────────
      map.on("click", LYR_CLUSTERS, (e) => {
        const feat = e.features?.[0];
        if (!feat) return;
        const src = map.getSource(SRC) as GeoJSONSource;
        const clusterId = feat.properties?.cluster_id as number;
        src.getClusterExpansionZoom(clusterId).then((zoom) => {
          const [lng, lat] = (feat.geometry as GeoJSON.Point).coordinates;
          map.easeTo({ center: [lng, lat], zoom: zoom + 0.2, duration: 600 });
        });
      });

      map.on("click", LYR_POINTS, (e) => {
        const feat = e.features?.[0];
        if (!feat) return;
        const id = String(feat.properties?.id);
        onSelectRef.current(id);
      });

      // Hover popup on points
      const popup = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 14,
        className: "lm-popup",
      });
      hoverPopupRef.current = popup;
      map.on("mouseenter", LYR_POINTS, (e) => {
        const feat = e.features?.[0];
        if (!feat) return;
        map.getCanvas().style.cursor = "pointer";
        const p = feat.properties as PointProps;
        const coords = (feat.geometry as GeoJSON.Point).coordinates as [number, number];
        popup.setLngLat(coords).setHTML(buildPopupHtml(p)).addTo(map);
      });
      map.on("mouseleave", LYR_POINTS, () => {
        map.getCanvas().style.cursor = "";
        popup.remove();
      });
      map.on("mouseenter", LYR_CLUSTERS, () => (map.getCanvas().style.cursor = "pointer"));
      map.on("mouseleave", LYR_CLUSTERS, () => (map.getCanvas().style.cursor = ""));

      setStyleReady(true);
    });

    return () => {
      userMarkerRef.current?.remove();
      userMarkerRef.current = null;
      hoverPopupRef.current?.remove();
      hoverPopupRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ─────────────────────────── Push data + first fitBounds ───────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    const src = map.getSource(SRC) as GeoJSONSource | undefined;
    if (!src) return;
    src.setData(fc);

    if (fc.features.length > 0 && !fitDoneRef.current) {
      const bounds = new maplibregl.LngLatBounds();
      fc.features.forEach((f) => {
        const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
        bounds.extend([lng, lat]);
      });
      map.fitBounds(bounds as LngLatBoundsLike, {
        padding: { top: 60, right: 60, bottom: 60, left: 60 },
        duration: 900,
        maxZoom: 11,
      });
      fitDoneRef.current = true;
    }
  }, [fc, styleReady]);

  // ─────────────────────────── User location marker ──────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    if (!userCoords) {
      userMarkerRef.current?.remove();
      userMarkerRef.current = null;
      userFlewToRef.current = false;
      return;
    }
    const lngLat: [number, number] = [userCoords.lng, userCoords.lat];
    if (!userMarkerRef.current) {
      const el = document.createElement("div");
      el.className = "lm-userloc";
      el.innerHTML = `
        <span class="lm-userloc__halo" aria-hidden="true"></span>
        <span class="lm-userloc__core" aria-hidden="true"></span>
      `;
      userMarkerRef.current = new maplibregl.Marker({
        element: el,
        anchor: "center",
        subpixelPositioning: true,
        pitchAlignment: "viewport",
        rotationAlignment: "viewport",
      })
        .setLngLat(lngLat)
        .addTo(map);
    } else {
      userMarkerRef.current.setLngLat(lngLat);
    }
    if (!userFlewToRef.current) {
      map.flyTo({
        center: lngLat,
        zoom: Math.max(map.getZoom(), 11.5),
        speed: 1.0,
        curve: 1.4,
        essential: true,
      });
      userFlewToRef.current = true;
    }
  }, [userCoords, styleReady]);

  // ─────────────────────────── Selected highlight + flyTo ────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !styleReady) return;
    const filter = ["==", ["get", "id"], selectedId ?? "__none__"] as never;
    if (map.getLayer(LYR_SELECTED)) map.setFilter(LYR_SELECTED, filter);
    if (map.getLayer(LYR_SELECTED_RING)) map.setFilter(LYR_SELECTED_RING, filter);
    if (selectedId) {
      const feat = fc.features.find((f) => f.id === selectedId);
      if (feat) {
        const [lng, lat] = (feat.geometry as GeoJSON.Point).coordinates;
        map.flyTo({
          center: [lng, lat],
          zoom: Math.max(map.getZoom(), 13.5),
          speed: 1.1,
          curve: 1.4,
          essential: true,
        });
      }
    }
  }, [selectedId, fc, styleReady]);

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="absolute inset-0" />
      <div className="pointer-events-none absolute left-4 top-4 z-10">
        <div className="pointer-events-auto">
          <Legend />
        </div>
      </div>
      <div className="pointer-events-none absolute left-4 top-20 z-10">
        <div className="pointer-events-auto">
          <LocateButton
            active={locateActive}
            busy={locateBusy}
            onClick={onLocateClick}
            label={t("nearMe")}
            stopLabel={t("stopLocate")}
          />
        </div>
      </div>
      {locations.length > 0 && (
        <div className="pointer-events-none absolute right-4 bottom-12 z-10">
          <div className="glass rounded-full px-3 py-1.5 text-[11px] font-medium text-ink-700 shadow-soft">
            <span className="tabular-nums font-semibold text-ink-900">{locations.length}</span>{" "}
            {t("locations")}
          </div>
        </div>
      )}
    </div>
  );
}

function tierColorExpr(): maplibregl.ExpressionSpecification {
  return [
    "match",
    ["get", "tier"],
    "AC",
    BRAND.ac,
    "DC_FAST",
    BRAND.dcFast,
    "DC_ULTRA",
    BRAND.dcUltra,
    BRAND.primary,
  ];
}

function LocateButton({
  active,
  busy,
  onClick,
  label,
  stopLabel,
}: {
  active: boolean;
  busy: boolean;
  onClick: () => void;
  label: string;
  stopLabel: string;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      title={active ? stopLabel : label}
      className={
        "group glass inline-flex items-center gap-2 rounded-full px-3 py-2 text-[12px] font-medium shadow-soft transition " +
        (active ? "ring-2 ring-brand-300 text-brand-700" : "text-ink-800 hover:text-brand-700")
      }
    >
      <span className="relative grid h-5 w-5 place-items-center">
        {busy ? (
          <svg
            className="animate-spin text-brand-600"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
          >
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.25" />
            <path
              d="M21 12a9 9 0 0 0-9-9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle cx="12" cy="12" r="3.4" fill={active ? "#1B723B" : "#26262E"} />
            <circle cx="12" cy="12" r="7.2" stroke={active ? "#1B723B" : "#26262E"} strokeWidth="1.6" />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke={active ? "#1B723B" : "#26262E"} strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        )}
      </span>
      <span>{active ? stopLabel : label}</span>
    </button>
  );
}

function buildPopupHtml(p: PointProps): string {
  const color =
    p.tier === "AC" ? BRAND.ac : p.tier === "DC_FAST" ? BRAND.dcFast : BRAND.dcUltra;
  const tierLabel = p.tier === "AC" ? "AC" : "DC";
  const groupBadge =
    p.groupCount > 1 ? `<span class="lm-popup__group">+${p.groupCount - 1}</span>` : "";
  return `
    <div class="lm-popup__inner">
      <div class="lm-popup__title">${escapeHtml(p.name)} ${groupBadge}</div>
      <div class="lm-popup__meta">
        <span class="lm-popup__chip" style="background:${color}1a;color:${color}">${tierLabel}</span>
        <span class="lm-popup__kw">${formatKw(p.kw)}</span>
      </div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
